import { BiCardDataView } from '@euler/app/flows/bi-dashboards/dashboard/functions/useBiCardData';
import { BiDateFilterUse } from '@euler/model/bi/dashboard';
import { biDisplayFormat } from '@euler/model/bi/format';
import { BiDateWindow } from '@euler/model/bi/query';
import {
  BiCell,
  BiFieldRole,
  BiFieldType,
  BiQueryResult,
  BiResultColumn,
} from '@euler/model/bi/result';
import { BiFormat } from '@euler/model/bi/viz';

// The columns and helpers the gallery's hand-written fixtures share.

const kAsOf = '2026-10-17T06:30:00.000Z';

function dimension(
  key: string,
  zh: string,
  type = BiFieldType.String,
): BiResultColumn {
  return { key, label: { zh, en: key }, type, role: BiFieldRole.Dimension };
}

/** A coded dimension: rows hold codes, the column carries their labels. */
function coded(
  key: string,
  zh: string,
  valueLabels: Record<string, string>,
): BiResultColumn {
  return { ...dimension(key, zh), valueLabels };
}

function measure(
  key: string,
  zh: string,
  format: BiFormat,
  side?: number,
): BiResultColumn {
  return {
    key,
    label: { zh, en: key },
    type: BiFieldType.Number,
    role: BiFieldRole.Measure,
    format: biDisplayFormat(format),
    ...(side === undefined ? {} : { side }),
  };
}

export const kGalleryFields = {
  month: dimension('created_at__month', '月份', BiFieldType.Date),
  day: dimension('created_at__day', '日期', BiFieldType.Date),
  serviceType: dimension('service_type', '服务类型'),
  item: dimension('item_name', '服务项目'),
  technician: dimension('technician_name', '技师'),
  brand: dimension('vehicle_brand', '车辆品牌'),
  jobName: dimension('job_name', '施工项目'),
  orderStatus: coded('status', '工单状态', {
    pending: '待处理',
    inprogress: '进行中',
    finished: '已完成',
    cancelled: '已取消',
  }),
  revenue: measure('revenue__sum', '营收', BiFormat.Currency),
  partsRevenue: measure('parts_revenue__sum', '配件营收', BiFormat.Currency),
  laborRevenue: measure('labor_revenue__sum', '工时营收', BiFormat.Currency),
  target: measure('target_revenue__sum', '目标营收', BiFormat.Currency),
  avgTicket: measure('avg_ticket', '客单价', BiFormat.Currency),
  orders: measure('order_count', '工单数', BiFormat.Integer),
  newCustomers: measure('new_customers', '新客户', BiFormat.Integer),
  returning: measure('returning_customers', '老客户', BiFormat.Integer),
  returnRate: measure('return_rate', '复购率', BiFormat.Percent),
  jobTime: measure('job_minutes__avg', '平均施工时长', BiFormat.Duration),
  rating: measure('rating__avg', '平均评分', BiFormat.Decimal),
  jobs: measure('job_count', '施工项目数', BiFormat.Integer),
  finishedJobs: measure('finished_job_count', '已完工项目数', BiFormat.Integer),
  reworks: measure('rework_count', '返工次数', BiFormat.Integer),
  lastMonthJobs: measure(
    'q1__job_count',
    '施工项目数（上月）',
    BiFormat.Integer,
    0,
  ),
  thisMonthJobs: measure(
    'q2__job_count',
    '施工项目数（本月）',
    BiFormat.Integer,
    1,
  ),
};

/** January to October 2026, as a month dimension reads. */
export const kGalleryMonths = Array.from(
  { length: 10 },
  (_, i) => `2026-${String(i + 1).padStart(2, '0')}`,
);

export const kGalleryServiceTypes = ['常规保养', '钣金喷漆', '轮胎更换'];

/** Order statuses as the rows store them: codes, labelled on screen. */
export const kGalleryOrderStatuses = [
  'finished',
  'inprogress',
  'pending',
  'cancelled',
];

/** Item names long enough to be cut off on a phone. */
export const kGalleryLongItems = [
  '全合成机油更换（含机油滤芯与工时）',
  '四轮定位及轮胎动平衡检测',
  '前后刹车片更换（原厂配件）',
  '空调系统深度清洗与滤芯更换',
  '发动机舱精洗护理套餐',
  '变速箱油循环机更换',
];

export function galleryResult(
  columns: BiResultColumn[],
  rows: BiCell[][],
  window?: BiDateWindow,
): BiQueryResult {
  return {
    columns,
    rows,
    truncated: false,
    asOf: kAsOf,
    ...(window ? { window } : {}),
  };
}

/** A card's data as it arrives, with no dashboard date range by default. */
export function galleryReady(
  current: BiQueryResult,
  options: { previous?: BiQueryResult; dateFilter?: BiDateFilterUse } = {},
): BiCardDataView {
  return {
    kind: 'ready',
    result: options.previous
      ? { current, previous: options.previous }
      : { current },
    dateFilter: options.dateFilter ?? BiDateFilterUse.None,
  };
}

/** Yuan as the cents a currency cell holds. */
export function cents(yuan: number): number {
  return Math.round(yuan * 100);
}
